import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Card,
  Modal,
  Form,
  Input,
  Tabs,
  Badge,
  Spin,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { SourceChapter } from '../../types';
import { sourceStoriesApi, CreateSourceChapterRequest, UpdateSourceChapterRequest } from '../../services/api';
import { adaptationApi } from '../../services/api/adaptation';
import type { AdaptationProgress } from '../../types/adaptation';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate, truncateText } from '../../utils';
import OverviewTab from './OverviewTab';
import ProcessTab from './ProcessTab';
import CharactersTab from './CharactersTab';
import GlossaryTab from './GlossaryTab';
import EpisodesTab from './EpisodesTab';
import FilmArea from './FilmArea';
import AdaptationWorkbench from './workbench/AdaptationWorkbench';
import { resolveActiveTab, progressFor, FilmProgress } from './filmTabView';
import { storyTabOf } from './storyTabMode';
import './storyPage.css';

const { TextArea } = Input;

/**
 * Chấm trạng thái trên nhãn Tab "Chuyển thể" (đợt 260826, Epic 4) — fetch ĐÚNG 1 lần lúc mount, không
 * polling/refetch khi đổi tab con. Đánh đổi có chủ đích để giữ "rẻ, chỉ 1 request" (không đụng trang
 * danh sách Stories tổng): badge có thể lệch nếu người dùng duyệt/bỏ duyệt trong lúc đang ở trang.
 */
const AdaptationTabLabel: React.FC<{ progress: AdaptationProgress | null }> = ({ progress }) => {
  const badge = !progress?.hasOutline ? null
    : progress.outlineStatus === 'DRAFT' ? <Badge status="warning" />
    : progress.outlineStatus === 'APPROVED' ? <Badge status="success" />
    : null;

  return <Space size={4}>Chuyển thể{badge}</Space>;
};

const SourceStoryDetail: React.FC = () => {
  const { id: storyId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // 261010 U1 — khung chế độ do storyTabOf quyết: Narration bị giấu (mặc định film; chỉ `?mode=narration` tường minh mới vào narration).
  const parsedTab = storyTabOf({ mode: searchParams.get('mode'), tab: searchParams.get('tab') });
  const mode = parsedTab.mode;
  // 261023 E3-P3h1 (K9 / V1): `progress` lấy MỘT lần ở đây (nhãn tab «Chuyển thể» dùng chung, không gọi thêm). Tab mặc định của Film Mode chọn sau khi có dữ liệu và ĐÓNG BĂNG theo truyện.
  // R4/F4: gắn progress với `storyId` của chính nó — đổi truyện cùng route thì progress của truyện trước KHÔNG được dùng để quyết tab.
  const [progressState, setProgressState] = useState<{ id: string; v: AdaptationProgress | 'failed' } | null>(null);
  const progress = progressFor(storyId, progressState as { id: string; v: FilmProgress } | null) as AdaptationProgress | 'failed' | null;
  useEffect(() => {
    if (!storyId) return undefined;
    let active = true;
    adaptationApi.progress(storyId).then((p) => { if (active) setProgressState({ id: storyId, v: p }); }).catch(() => { if (active) setProgressState({ id: storyId, v: 'failed' }); });
    return () => { active = false; };
  }, [storyId]);
  // Hợp nhất: storyTabOf xác nhận `?tab=` có hợp lệ với chế độ không (tab sai chế độ ⇒ coi như CHƯA chọn); resolveActiveTab quyết tab mặc định của Film
  // (0 chương ⇒ «Chuyển thể», còn lại «Film»; Narration ⇒ «Overview») khi người dùng chưa chọn tab hợp lệ.
  const tabParam = searchParams.get('tab');
  const explicitTab = tabParam && parsedTab.tab === tabParam ? tabParam : null;
  const frozenTab = useRef<{ storyId: string | undefined; tab: string } | null>(null);
  const resolved = resolveActiveTab({ mode, tabParam: explicitTab, progress: progress as FilmProgress });
  if (!explicitTab && mode === 'film' && !resolved.pending && (!frozenTab.current || frozenTab.current.storyId !== storyId)) frozenTab.current = { storyId, tab: resolved.tab };
  const frozen = frozenTab.current;
  const activeTab = explicitTab || (mode === 'film' && frozen && frozen.storyId === storyId ? frozen.tab : resolved.tab);
  const tabsPending = !explicitTab && mode === 'film' && resolved.pending;

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [editingChapter, setEditingChapter] = useState<SourceChapter | null>(null);
  const [viewingChapter, setViewingChapter] = useState<SourceChapter | null>(null);
  const [form] = Form.useForm();
  const [filmPlanOpen, setFilmPlanOpen] = useState(false); // đang mở 1 Film plan → ẩn toggle/tabs cho gọn tầng nav

  const handleTabChange = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key); // giữ nguyên mode khi đổi tab (Film Mode giờ cũng có tab)
    if (key !== 'adaptation') { next.delete('stage'); next.delete('ep'); } // rời tab Chuyển thể ⇒ bỏ `stage`, `ep` (261005 E1-P1/P2)
    setSearchParams(next);
  };

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  // Fetch story detail
  const {
    data: storyData,
    loading: storyLoading,
    refresh: refreshStory,
  } = useApi(
    useCallback(() => {
      if (!storyId) return Promise.reject(new Error('Story ID is required'));
      return sourceStoriesApi.getSourceStoryDetail(storyId);
    }, [storyId]),
    { immediate: true }
  );

  // Fetch chapters
  const getChapters = useCallback(() => {
    if (!storyId) {
      return Promise.resolve({ data: [], totalRecord: 0 });
    }
    return sourceStoriesApi.getSourceChapters(storyId, {
      pageIndex: 0,
      pageSize: 500,
    });
  }, [storyId]);

  const {
    data: chaptersData,
    loading: chaptersLoading,
    refresh: refreshChapters,
  } = useApi(getChapters, { immediate: true });

  useEffect(() => {
    // Only refresh if immediate is false, otherwise useApi handles it
    // But since we want to ensure chapters are fresh when entering detail, 
    // and useApi with immediate:true already does it, we can remove this.
  }, [refreshChapters]);

  const handleCreate = () => {
    setEditingChapter(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (chapter: SourceChapter) => {
    setEditingChapter(chapter);
    form.setFieldsValue({
      name: chapter.name,
      content: chapter.content,
      link: chapter.link,
    });
    setIsModalVisible(true);
  };

  const handleView = (chapter: SourceChapter) => {
    setViewingChapter(chapter);
    setIsViewModalVisible(true);
  };

  const handleDelete = async (chapter: SourceChapter) => {
    if (!storyId) return;
    await executeAsync(
      () => sourceStoriesApi.deleteSourceChapter(storyId, chapter.id),
      { successMessage: 'Chapter deleted successfully!' }
    );
    refreshChapters();
  };

  const handleSubmit = async (values: CreateSourceChapterRequest) => {
    if (!storyId) return;

    const operation = editingChapter
      ? async () => {
          const response = await sourceStoriesApi.updateSourceChapter(
            storyId,
            editingChapter.id,
            values as UpdateSourceChapterRequest
          );
          return response.data;
        }
      : async () => {
          const response = await sourceStoriesApi.createSourceChapter(storyId, values);
          return response.data;
        };

    const result = await executeAsync(
      operation,
      {
        successMessage: editingChapter
          ? 'Chapter updated successfully!'
          : 'Chapter created successfully!'
      }
    );

    if (result) {
      setIsModalVisible(false);
      form.resetFields();
      refreshChapters();
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'orderIndex',
      key: 'orderIndex',
      width: 60,
      // orderIndex đã là SỐ CHƯƠNG 1-based (chương đầu = 1: BE `getMaxOrderIndex` rỗng ⇒ 0, +1; handoff gán = số tập) — không cộng thêm 1.
      render: (index: number) => <span className="sp-num">{index}</span>,
    },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => text || '-',
    },
    {
      title: 'Nội dung đầu',
      dataIndex: 'content',
      key: 'content',
      render: (text: string) => text ? truncateText(text, 100) : '-',
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      render: (link: string) => link ? (
        <a href={link} target="_blank" rel="noopener noreferrer">
          {truncateText(link, 30)}
        </a>
      ) : '-',
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      render: (_: unknown, record: SourceChapter) => (
        <Space>
          <Tooltip title="Xem nội dung">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              aria-label="Xem nội dung"
            />
          </Tooltip>
          <Tooltip title="Sửa">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              aria-label="Sửa"
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this chapter?"
            onConfirm={() => handleDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <span style={{ display: 'inline-flex' }}>
              <Tooltip title="Xoá">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label="Xoá"
                />
              </Tooltip>
            </span>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const story = storyData?.data?.story;
  const storyVoices = storyData?.data?.storyVoices;

  // ── Tab nguồn dùng chung cho cả Narration & Film (Overview + Chapters) ──
  const overviewItem = {
    key: 'overview',
    label: mode === 'film' ? 'Tổng quan' : 'Overview',
    children: story ? (
      <OverviewTab
        story={story}
        storyVoices={storyVoices}
        totalChapters={chaptersData?.totalRecord || 0}
        onRefreshStory={refreshStory}
        loading={storyLoading}
        hideNarration={mode === 'film'}
      />
    ) : (
      <Card loading={storyLoading} />
    ),
  };
  const chaptersItem = {
    key: 'chapters',
    label: mode === 'film' ? 'Chương' : 'Chapters',
    children: (
      <div className="sp-pane">
        <section className="sp-sec">
          <header className="sp-sec-h">
            <h2>Chương{chaptersData?.totalRecord ? ` · ${chaptersData.totalRecord}` : ''}</h2>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>Thêm chương</Button>
          </header>
          <Table
            columns={columns}
            dataSource={chaptersData?.data || []}
            rowKey="id"
            loading={chaptersLoading}
            pagination={false}
          />
        </section>
      </div>
    ),
  };

  const narrationTabItems = [
    overviewItem,
    chaptersItem,
    { key: 'characters', label: 'Characters', children: storyId ? <CharactersTab storyId={storyId} /> : null },
    { key: 'glossary', label: 'Glossary', children: storyId ? <GlossaryTab storyId={storyId} /> : null },
    { key: 'processes', label: 'Process', children: storyId ? <ProcessTab storyId={storyId} chapters={chaptersData?.data || []} /> : null },
    { key: 'episodes', label: 'Episodes', children: storyId ? <EpisodesTab storyId={storyId} /> : null },
  ];
  const filmTabItems = [
    overviewItem,
    // ⓪ đứng TRƯỚC Chapters vì nó SINH RA các chương (mỗi tập một chương); để sau thì đọc ngược luồng.
    // Overview vẫn ở đầu — đó là hồ sơ truyện (tên, style, giọng), không phải một bước làm việc.
    { key: 'adaptation', label: storyId ? <AdaptationTabLabel progress={progress === 'failed' ? null : progress} /> : 'Chuyển thể', children: storyId ? <AdaptationWorkbench storyId={storyId} storyName={story?.name} storyLink={story?.link} /> : null },
    chaptersItem,
    { key: 'film', label: '🎬 Film', children: storyId ? <FilmArea storyId={storyId} active={activeTab === 'film'} onPlanOpenChange={setFilmPlanOpen} /> : null },
  ];

  // Đang ở trong 1 Film plan → ẩn toggle Narration/Film + tab bar (bớt tầng nav; bấm "← Plans" để ra).
  const inPlan = mode === 'film' && activeTab === 'film' && filmPlanOpen;

  return (
    <div className="story-chrome sp-page">
      <nav className="sp-crumb" aria-label="breadcrumb">
        <Link to="/source-stories"><ArrowLeftOutlined /> Quản lý nội dung</Link>
        <span className="sep">/</span>
        <span className="cur">{story?.name || 'Chi tiết truyện'}</span>
      </nav>

      {tabsPending ? <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div> : <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={mode === 'film' ? filmTabItems : narrationTabItems}
        renderTabBar={inPlan ? () => <></> : undefined}
      />}

      {/* Create/Edit Chapter Modal */}
      <Modal
        title={editingChapter ? 'Edit Chapter' : 'Add New Chapter'}
        open={isModalVisible}
      onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
      footer={null}
      width={700}
      centered
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '24px'
        }
      }}
    >
      <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Chapter Name"
          >
            <Input placeholder="Enter chapter name (optional)..." />
          </Form.Item>

          <Form.Item
            name="link"
            label="Link"
          >
            <Input placeholder="Enter original chapter link (optional)..." />
          </Form.Item>

          <Form.Item
            name="content"
            label="Content"
          >
            <TextArea
              rows={15}
              placeholder="Paste chapter content here..."
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
              >
                {editingChapter ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View Chapter Content Modal */}
      <Modal
        title={`Chapter: ${viewingChapter?.name || `#${viewingChapter?.orderIndex ?? 0}`}`}
        open={isViewModalVisible}
        onCancel={() => setIsViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {viewingChapter && (
          <div>
            {viewingChapter.link && (
              <div style={{ marginBottom: 16 }}>
                <strong>Link:</strong>{' '}
                <a href={viewingChapter.link} target="_blank" rel="noopener noreferrer">
                  {viewingChapter.link}
                </a>
              </div>
            )}
            <div
              style={{
                maxHeight: '60vh',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: 14,
                lineHeight: 1.6,
                padding: 16,
                backgroundColor: '#f5f5f5',
                borderRadius: 8,
              }}
            >
              {viewingChapter.content || 'No content'}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SourceStoryDetail;
